import React, { useEffect, useState, useMemo } from "react";
import { ArrowDownTrayIcon, ChevronRightIcon, HomeIcon, FolderOpenIcon, MagnifyingGlassIcon, ArrowPathIcon, ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/outline";
import { FolderIcon } from "@heroicons/react/24/solid";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import DocumentPreviewModal from "src/components/embedTypes/basic/Document/DocumentPreviewModal";
import { getFileIcon } from "src/helpers/documentHelper";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";
import UserDropdown from "src/components/shared/UserDropdown";
import { logoutRedirect } from "src/async/fetch";
import { useSiteSettings } from "src/context/SiteSettingsContext";

const Storage = () => {
  const { site } = useSiteSettings();
  const { finishSwitching } = useWorkspaceSwitching();
  const { link_id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [navigationStack, setNavigationStack] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [activeDownloads, setActiveDownloads] = useState({});

  const highlightColor = site?.highlight_color || "#0062A2";

  const loadUser = async () => {
    try {
      const { data: resData } = await apiRequest("post", "/portal/users/self/details", {
        body: { token: localStorage.getItem("accessToken"), access_token: localStorage.getItem("accessToken") },
      });
      setUser(resData.data);
    } catch (error) {}
  };

  const getStorage = async () => {
    setLoading(true);
    try {
      const res = await apiRequest("get", `portal/storage/:link_id`, { params: { link_id } });
      const data = res.data?.data || [];
      setItems(Array.isArray(data) ? data : [data]);
    } catch (error) {
      if (user?._id || localStorage.getItem("accessToken")) {
        navigate("/");
      } else {
        navigate("/login", { state: { from: window.location.pathname } });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    finishSwitching();
    loadUser();
    getStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link_id]);

  const handleDownload = async (e, item) => {
    e.stopPropagation();
    if (!item) return;

    setActiveDownloads((prev) => ({ ...prev, [item._id]: true }));

    try {
      const response = await apiRequest(
        "get",
        "portal/storage/:link_id/download/:storage_id",
        { params: { link_id, storage_id: item._id } },
        {
          is_abort: false,
          responseType: "blob",
        },
      );

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.type === "folder" ? `${item.name}.zip` : item.file.original_name || item.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setActiveDownloads((prev) => {
        const newState = { ...prev };
        delete newState[item._id];
        return newState;
      });
    }
  };

  const visibleItems = useMemo(() => {
    if (searchQuery) {
      return items.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    if (currentFolderId) {
      return items.filter((item) => item.parent_id === currentFolderId);
    }

    const allIds = items.map((i) => i._id);
    return items.filter((item) => !allIds.includes(item.parent_id));
  }, [items, currentFolderId, searchQuery]);

  const logout = () => {
    logoutRedirect();
  };

  const userMenu = {
    dropdownData: [
      {
        _id: "logout",
        icon: ArrowRightStartOnRectangleIcon,
        label: "Logout",
        onclick: logout,
        standout: true,
      },
    ],
  };

  if (loading) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-gray-50/50">
        <Preloader />
      </div>
    );
  }

  return (
    <div className="h-full">
      <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 px-6 py-3 backdrop-blur-md">
        <div className="mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {site?.image_logo ? (
              <img
                src={site.image_logo}
                alt="Logo"
                className="h-9 w-auto object-contain"
              />
            ) : (
              <div
                className="flex h-10 w-10 items-center justify-center rounded bg-gray-100 font-bold"
                style={{ color: highlightColor }}>
                {site?.site_name?.charAt(0) || "S"}
              </div>
            )}
          </div>

          {user?._id && (
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm transition hover:bg-gray-50 active:scale-95">
                <HomeIcon className="h-4 w-4 text-highlightColor" />
                <span className="hidden text-highlightColor sm:inline">Back to Dashboard</span>
              </button>
              <div className="h-8 border-r" />
              <UserDropdown
                data={userMenu}
                user={user}
                xPlacement={"right"}
              />
            </div>
          )}
        </div>
      </nav>

      <main className="mx-auto max-w-7xl p-6 sm:p-10">
        <div className="mb-8 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="flex items-center gap-1 text-sm">
            <button
              onClick={() => {
                setCurrentFolderId(null);
                setNavigationStack([]);
                setSearchQuery("");
              }}
              className={`flex items-center gap-2 rounded-md px-3 py-2 transition hover:bg-gray-200 ${!currentFolderId ? "bg-white font-bold text-gray-900 shadow-sm ring-1 ring-gray-200" : "text-gray-500"}`}>
              <FolderOpenIcon className="h-4 w-4" /> Storage
            </button>
            {navigationStack.map((folder, idx) => (
              <React.Fragment key={folder._id}>
                <ChevronRightIcon className="h-4 w-4 text-gray-300" />
                <button
                  onClick={() => {
                    const newStack = navigationStack.slice(0, idx + 1);
                    setNavigationStack(newStack);
                    setCurrentFolderId(folder._id);
                    setSearchQuery("");
                  }}
                  className={`max-w-[150px] truncate rounded-md px-3 py-2 transition hover:bg-gray-200 ${idx === navigationStack.length - 1 ? "bg-white font-bold text-gray-900 shadow-sm ring-1 ring-gray-200" : "text-gray-500"}`}>
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              className="w-full rounded-xl border-gray-200 bg-white py-2.5 pl-11 pr-4 text-sm shadow-sm transition focus:border-transparent focus:ring-2"
              style={{ "--tw-ring-color": highlightColor }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-200/20">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b bg-highlightColor">
                <tr className="text-[11px] font-bold uppercase tracking-widest text-white">
                  <th className="px-8 py-5">Name</th>
                  <th className="px-8 py-5">Format</th>
                  <th className="px-8 py-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="py-24 text-center">
                      <div className="flex flex-col items-center gap-3 opacity-20">
                        <FolderIcon className="h-16 w-16" />
                        <p className="text-lg font-medium">No data found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleItems.map((item) => (
                    <tr
                      key={item._id}
                      className="animate-fade-in group cursor-pointer transition-all hover:bg-gray-50/80"
                      onClick={() => {
                        if (item.type === "folder") {
                          setNavigationStack([...navigationStack, item]);
                          setCurrentFolderId(item._id);
                          setSearchQuery("");
                        } else {
                          setPreviewItem(item);
                          setPreviewOpen(true);
                        }
                      }}>
                      <td className="px-8 py-4">
                        <div className="flex items-center gap-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 transition-all group-hover:scale-110 group-hover:bg-white group-hover:shadow-sm">
                            {item.type === "folder" ? <FolderIcon className="h-6 w-6 text-amber-400" /> : getFileIcon(item.file?.extension)}
                          </div>
                          <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">{item.name}</span>
                        </div>
                      </td>
                      <td className="px-8 py-4">
                        <span className="text-xs font-medium uppercase tracking-wider text-gray-400">{item.type === "folder" ? "Folder" : item.file.extension}</span>
                      </td>
                      <td className="px-8 py-4 text-right">
                        <button
                          onClick={(e) => handleDownload(e, item)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 text-gray-400 transition-all hover:bg-white hover:shadow-md active:scale-95">
                          {activeDownloads[item._id] ? (
                            <ArrowPathIcon
                              className="h-5 w-5 animate-spin"
                              style={{ color: highlightColor }}
                            />
                          ) : (
                            <ArrowDownTrayIcon className="h-5 w-5 group-hover:text-gray-600" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {previewItem && previewOpen && (
        <DocumentPreviewModal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          docs={[
            {
              _id: previewItem._id,
              name: previewItem.name,
              extension: previewItem.file?.extension,
              mime_type: previewItem.file?.mime_type,
              size_in_bytes: previewItem.file?.size_in_bytes,
            },
          ]}
          initialIndex={0}
          isStorage={true}
        />
      )}
    </div>
  );
};

export default Storage;
